import { _ } from "../../../core/polyglot/polyglot.js";
import {
	notBlank,
	required,
	validateAll,
	validEmail,
	validInternationalPhoneNumber,
} from "../../../core/widgets/forms/validators.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import FormBuilder from "../../../core/widgets/forms/FormBuilder.js";
import SignUpRequestInfo from "../../../models/SignUpRequestInfo.js";

/**
 * `SignUpRequestFormWidget` is a widget displaying input fields used by
 * potential new customers to sign up for a trial/demo version.
 *
 * @param {SignUpRequestInfo} signUpRequestInfo - Values to prefill the input
 *   fields with
 */
export default class SignUpRequestFormWidget extends Widget {
	constructor({
		submitCallback = () => {},
		signUpRequestInfo = new SignUpRequestInfo(),
	} = {}) {
		super(...arguments);

		this._submitCallback = submitCallback;
		this._signUpRequestInfo = signUpRequestInfo;

		this._initializeFormWidget();
	}

	getSignUpRequestInfo() {
		return this._signUpRequestInfo;
	}

	setSignUpRequestInfo({ signUpRequestInfo }) {
		this._signUpRequestInfo = signUpRequestInfo;
		this._initializeFormWidget();
		this.update();
	}

	renderContentOn(html) {
		if (!this._form) {
			return;
		}

		html.div(
			html.p(
				{ class: "lead" },
				_(
					"Complete the form and one of the team will be in touch soon.",
				),
			),
		);

		html.div({ klass: "signup" }, this._form);
	}

	_initializeFormWidget() {
		this._signUpRequestInfo ||= new SignUpRequestInfo();

		this._form = new FormBuilder({
			saveAction: this._submitCallback,
			saveLabel: _("Request Demo"),
			showCancel: false,
			model: this._signUpRequestInfo,
			dataTestName: "sign-up-info",
		});

		this._form.input({
			name: "firstName",
			label: _("First name"),
			accessor: this._signUpRequestInfo.getFirstName.bind(
				this._signUpRequestInfo,
			),
			mutator: this._signUpRequestInfo.setFirstName.bind(
				this._signUpRequestInfo,
			),
			validator: notBlank(),
		});

		this._form.input({
			name: "lastName",
			label: _("Last name"),
			accessor: this._signUpRequestInfo.getLastName.bind(
				this._signUpRequestInfo,
			),
			mutator: this._signUpRequestInfo.setLastName.bind(
				this._signUpRequestInfo,
			),
			validator: notBlank(),
		});

		this._form.append((html) =>
			html.p(
				_(
					"Please note we cannot send emails to generic email addresses (such as info@ or office@). To ensure deliverability, please use your business or personal address.",
				),
			),
		);

		this._form.input({
			name: "email",
			label: _("E-mail"),
			accessor: this._signUpRequestInfo.getEmail.bind(
				this._signUpRequestInfo,
			),
			mutator: this._signUpRequestInfo.setEmail.bind(
				this._signUpRequestInfo,
			),
			validator: validEmail(),
		});

		this._form.input({
			name: "phone",
			label: _("Phone number"),
			accessor: this._signUpRequestInfo.getPhone.bind(
				this._signUpRequestInfo,
			),
			mutator: this._signUpRequestInfo.setPhone.bind(
				this._signUpRequestInfo,
			),
			validator: validateAll(notBlank(), validInternationalPhoneNumber()),
		});

		this._form.input({
			name: "companyName",
			label: _("Company"),
			accessor: this._signUpRequestInfo.getCompanyName.bind(
				this._signUpRequestInfo,
			),
			mutator: this._signUpRequestInfo.setCompanyName.bind(
				this._signUpRequestInfo,
			),
			validator: notBlank(),
		});

		this._setupCompanySizeField();
		this._setupBusinessOperationTypeField();

		this._form.input({
			name: "postcode",
			label: _("Postcode"),
			accessor: this._signUpRequestInfo.getPostcode.bind(
				this._signUpRequestInfo,
			),
			mutator: this._signUpRequestInfo.setPostcode.bind(
				this._signUpRequestInfo,
			),
			validator: notBlank(),
		});

		this._form.append((html) =>
			html.p(
				_(
					"In order to provide you with the information requested, we need to store and process your personal data.",
				),
			),
		);

		this._form.checkbox({
			text: _(
				"If you agree to allow Wolters Kluwer to store and process your personal data, please check the box.",
			),
			name: "allowStoringPersonalData",
			accessor: this._signUpRequestInfo.getAllowStoringPersonalData.bind(
				this._signUpRequestInfo,
			),
			mutator: this._signUpRequestInfo.setAllowStoringPersonalData.bind(
				this._signUpRequestInfo,
			),
			validator: required(),
		});

		this._form.checkbox({
			text: _(
				"Wolters Kluwer would like to contact you about our products and services, as well as other products/services that may be of interest to you. If you consent to us contacting you for this purpose, please check the box.You may unsubscribe from these communications at any time.",
			),
			name: "allowBeingContactedForMarketing",
			accessor:
				this._signUpRequestInfo.getAllowBeingContactedForMarketing.bind(
					this._signUpRequestInfo,
				),
			mutator:
				this._signUpRequestInfo.setAllowBeingContactedForMarketing.bind(
					this._signUpRequestInfo,
				),
		});

		this._form.append((html) =>
			html.p(
				_(
					"By submitting the form you agree to allow Wolters Kluwer to store and process your personal data.",
				),
				html.br(),
				_(
					"For more information, please review our {privacyNoticeLink}.",
					{
						privacyNoticeLink: (html) =>
							html.a(
								{
									href: "https://www.wolterskluwer.com/en-gb/solutions/software-tax-accounting/privacy-notice",
								},
								_("privacy notice"),
							),
					},
				),
			),
		);
	}

	_setupCompanySizeField() {
		const companySizes = ["1-5", "6-35", "36+"];

		const items = companySizes.map((companySize) => ({
			value: companySize,
			label: companySize,
		}));

		this._form.newDropDownField({
			label: _("Company Size"),
			name: "companySize",
			dataTestName: "company-size",
			items,
			noDefaultSelection: true,
			validator: notBlank(),
			accessor: this._signUpRequestInfo.getCompanySize.bind(
				this._signUpRequestInfo,
			),
			onChange: this._signUpRequestInfo.setCompanySize.bind(
				this._signUpRequestInfo,
			),
			mutator: this._signUpRequestInfo.setCompanySize.bind(
				this._signUpRequestInfo,
			),
			selected: this._signUpRequestInfo.getCompanySize(),
		});
	}

	_setupBusinessOperationTypeField() {
		const businessOperationTypes = {
			InPractice: _("In practice"),
			InBusiness: _("In business"),
		};

		const items = Object.keys(businessOperationTypes).map(
			(businessOperationType) => ({
				value: businessOperationType,
				label: businessOperationTypes[businessOperationType],
			}),
		);

		this._form.newDropDownField({
			label: _("Company Type"),
			name: "businessOperationType",
			dataTestName: "business-operation-type",
			items,
			noDefaultSelection: true,
			validator: notBlank(),
			accessor: this._signUpRequestInfo.getBusinessOperationType.bind(
				this._signUpRequestInfo,
			),
			mutator: this._signUpRequestInfo.setBusinessOperationType.bind(
				this._signUpRequestInfo,
			),
			onChange: this._signUpRequestInfo.setBusinessOperationType.bind(
				this._signUpRequestInfo,
			),
			selected: this._signUpRequestInfo.getBusinessOperationType(),
		});
	}
}
